import { CalendarClock, Info, Users, Wand2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AutoAllocationPlan } from '@obraplan/core/domain/planning/auto-allocation';
import type { BoardDay } from '@obraplan/core/domain/planning/weekly-board';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import {
  countLabel,
  groupProposal,
  teamsAndPeopleLabel,
  type ProposalActivity,
  type ProposalPerson,
  type ProposalTeam,
} from './auto-allocation-groups';

/**
 * A proposta da semana, para revisar antes de gravar.
 *
 * O cronograma diz o que está em execução, o vínculo função ↔ disciplina diz quem faz, e
 * o calendário diz quanto cabe em cada dia — mas quem confirma é gente. O painel mostra
 * a proposta **inteira**, por funcionário e por dia, com quem ficou de fora e por quê:
 * ausência sem explicação é o que faz o planejador desconfiar da automação e voltar a
 * montar tudo na mão.
 *
 * **Na obra por equipe** (Fase 30, RN-432), as linhas da equipe se juntam nela: o nome, as
 * pessoas, as horas somadas e as frentes, com as horas dos membros somadas no dia. Quem não tem
 * equipe vem por pessoa, como sempre. A caixa da equipe marca e desmarca os membros dela; a de
 * cada pessoa continua lá, para tirar uma só.
 *
 * A linha que tem serviço diz qual — "ALVENARIA — 1º Pavimento · ALVENARIA NÃO ESTRUTURAL" —, e a
 * frente que o cronograma por item descreve por serviço tem uma linha por serviço (RN-434).
 *
 * Nenhum cálculo acontece aqui. A proposta chega pronta do domínio e é recalculada na
 * aplicação — a tela não soma nem redistribui nada além do que mostra.
 */
export interface AutoAllocationPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposal: AutoAllocationPlan | undefined;
  weekLabel: string;
  days: readonly BoardDay[];
  readOnly: boolean;
  isPending: boolean;
  /** A obra mede e planeja por equipe — o painel agrupa pela equipe da linha. */
  byTeam?: boolean;
  /** `undefined` = aplicar tudo; lista = apenas esses funcionários. */
  onApply: (resourceIds: readonly string[] | undefined) => void;
}

export function AutoAllocationPanel({
  open,
  onOpenChange,
  proposal,
  weekLabel,
  days,
  readOnly,
  isPending,
  byTeam = false,
  onApply,
}: AutoAllocationPanelProps) {
  const groups = useMemo(() => groupProposal(proposal, byTeam), [proposal, byTeam]);
  const [excluded, setExcluded] = useState<ReadonlySet<string>>(new Set());

  // Abrir zera a seleção; enquanto o painel está aberto, ela é de quem está revisando.
  // Recalcular a proposta não pode desmarcar ninguém: o `useLive` a recarrega a cada
  // gravação no banco, e a marcação da pessoa sumiria no meio da revisão.
  useEffect(() => {
    if (open) setExcluded(new Set());
  }, [open]);

  const allResourceIds = useMemo(
    () => new Set((proposal?.lines ?? []).map((line) => line.resourceId as string)),
    [proposal],
  );
  const selectedIds = [...allResourceIds].filter((resourceId) => !excluded.has(resourceId));
  const selectedQuarterHours = (proposal?.lines ?? [])
    .filter((line) => !excluded.has(line.resourceId))
    .reduce((sum, line) => sum + line.quarterHours, 0);
  const selectedTeams = groups.teams.filter((team) =>
    team.members.some((member) => !excluded.has(member.resourceId)),
  ).length;
  const selectedPeople = groups.people.filter((person) => !excluded.has(person.resourceId)).length;
  const isEmpty = groups.teams.length === 0 && groups.people.length === 0;

  const toggle = (resourceId: string): void => {
    setExcluded((current) => {
      const next = new Set(current);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.add(resourceId);
      return next;
    });
  };

  /** A equipe inteira: todos marcados viram todos desmarcados, e qualquer outro caso, todos. */
  const toggleTeam = (team: ProposalTeam): void => {
    setExcluded((current) => {
      const next = new Set(current);
      const allIncluded = team.members.every((member) => !current.has(member.resourceId));
      for (const member of team.members) {
        if (allIncluded) next.add(member.resourceId);
        else next.delete(member.resourceId);
      }
      return next;
    });
  };

  const audience =
    proposal === undefined
      ? ''
      : byTeam
        ? teamsAndPeopleLabel(proposal.teamCount, proposal.individualCount)
        : countLabel(proposal.resourceCount, 'funcionário', 'funcionários');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Alocação automática pelo cronograma</DialogTitle>
          <DialogDescription>
            {proposal === undefined
              ? 'Calculando a proposta…'
              : `${weekLabel} · ${countLabel(proposal.activityCount, 'atividade em execução', 'atividades em execução')} · proposta para ${audience}, ${Hours.ofQuarters(proposal.quarterHours).format()}.`}
          </DialogDescription>
        </DialogHeader>

        {proposal !== undefined && isEmpty && (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <CalendarClock className="size-8 text-(--color-content-muted)" aria-hidden="true" />
            <p className="font-medium">Nada a propor nesta semana</p>
            <p className="max-w-md text-sm text-(--color-content-muted)">
              Ou o cronograma não põe nenhuma atividade em execução nestes sete dias, ou as pessoas
              aptas já estão com a jornada cheia. Os motivos de cada um estão abaixo.
            </p>
          </div>
        )}

        {!isEmpty && (
          <div className="max-h-96 overflow-auto rounded-(--radius-card) border border-(--color-border-subtle)">
            <table className="w-full text-sm">
              <caption className="sr-only">
                {byTeam
                  ? 'Proposta de alocação por equipe, atividade e dia'
                  : 'Proposta de alocação por funcionário, atividade e dia'}
              </caption>
              <thead className="sticky top-0 bg-(--color-surface-raised)">
                <tr>
                  <th scope="col" className="p-2 text-left font-medium">
                    {byTeam ? 'Equipe ou funcionário e atividade' : 'Funcionário e atividade'}
                  </th>
                  {days.map((day) => (
                    <th
                      key={day.date}
                      scope="col"
                      className={cn(
                        'min-w-14 p-2 text-center text-xs font-medium',
                        !day.isWorkday && 'opacity-50',
                      )}
                    >
                      {day.weekdayLabel}
                    </th>
                  ))}
                  <th scope="col" className="p-2 text-right font-medium">
                    Semana
                  </th>
                </tr>
              </thead>
              <tbody>
                {groups.teams.map((team) => (
                  <TeamGroup
                    key={team.teamId}
                    team={team}
                    days={days}
                    excluded={excluded}
                    disabled={readOnly}
                    onToggleTeam={() => toggleTeam(team)}
                    onToggleMember={toggle}
                  />
                ))}
                {groups.people.map((person) => (
                  <ResourceGroup
                    key={person.resourceId}
                    group={person}
                    days={days}
                    included={!excluded.has(person.resourceId)}
                    disabled={readOnly}
                    onToggle={() => toggle(person.resourceId)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {proposal !== undefined && proposal.skipped.length > 0 && (
          <details className="rounded-(--radius-card) border border-(--color-border-subtle) p-3">
            <summary className="cursor-pointer text-sm font-medium">
              <Info className="mr-1 inline size-3.5" aria-hidden="true" />
              {countLabel(
                proposal.skipped.length,
                'recurso ficou de fora',
                'recursos ficaram de fora',
              )}
            </summary>
            <ul className="mt-2 flex flex-col gap-1 text-sm text-(--color-content-muted)">
              {proposal.skipped.map((skip) => (
                <li key={skip.resourceId}>
                  <span className="text-(--color-content)">{skip.resourceName}</span> —{' '}
                  {skip.description}
                </li>
              ))}
            </ul>
          </details>
        )}

        <DialogFooter>
          <p className="mr-auto text-sm text-(--color-content-muted)">
            {selectedIds.length === 0
              ? 'Nenhum funcionário selecionado.'
              : `${
                  byTeam
                    ? teamsAndPeopleLabel(selectedTeams, selectedPeople)
                    : countLabel(selectedIds.length, 'funcionário', 'funcionários')
                } · ${Hours.ofQuarters(selectedQuarterHours).format()}`}
          </p>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={readOnly || isPending || selectedIds.length === 0}
            onClick={() => onApply(excluded.size === 0 ? undefined : selectedIds)}
          >
            <Wand2 aria-hidden="true" />
            {isPending ? 'Aplicando…' : 'Aplicar proposta'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActivityRows({
  activities,
  days,
  dimmed,
}: {
  activities: readonly ProposalActivity[];
  days: readonly BoardDay[];
  dimmed: boolean;
}) {
  return (
    <>
      {activities.map((activity) => (
        <tr key={activity.key} className={cn(dimmed && 'opacity-50')}>
          <td className="py-1.5 pl-8 pr-2">
            {activity.activityLabel}
            {/* RN-434 — o serviço do item do cronograma: a linha diz o que se vai medir. */}
            {activity.serviceName !== undefined && (
              <span className="text-(--color-content-muted)"> · {activity.serviceName}</span>
            )}
          </td>
          {days.map((day) => {
            const quarterHours = activity.byDate.get(day.date);
            return (
              <td key={day.date} className="tabular p-1 text-center text-xs">
                {quarterHours === undefined ? (
                  <span className="text-(--color-content-muted)">—</span>
                ) : (
                  Hours.ofQuarters(quarterHours).format()
                )}
              </td>
            );
          })}
          <td className="tabular p-2 text-right text-xs">
            {Hours.ofQuarters(activity.quarterHours).format()}
          </td>
        </tr>
      ))}
    </>
  );
}

/**
 * A equipe: a caixa dela, as frentes com as horas dos membros somadas no dia, e as pessoas — cada
 * uma com a sua caixa, para tirar uma só da proposta.
 */
function TeamGroup({
  team,
  days,
  excluded,
  disabled,
  onToggleTeam,
  onToggleMember,
}: {
  team: ProposalTeam;
  days: readonly BoardDay[];
  excluded: ReadonlySet<string>;
  disabled: boolean;
  onToggleTeam: () => void;
  onToggleMember: (resourceId: string) => void;
}) {
  const included = team.members.filter((member) => !excluded.has(member.resourceId)).length;
  const all = included === team.members.length;
  const none = included === 0;

  // O estado "alguns" não existe no atributo: só na propriedade do elemento.
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (box.current !== null) box.current.indeterminate = !all && !none;
  }, [all, none]);

  return (
    <>
      <tr className={cn('bg-(--color-surface-muted)', none && 'opacity-50')}>
        <td className="p-2">
          <label className="flex items-center gap-2 font-medium">
            <input
              ref={box}
              type="checkbox"
              checked={all}
              disabled={disabled}
              onChange={onToggleTeam}
              aria-label={`Incluir a equipe ${team.teamName} na alocação automática`}
            />
            <Users className="size-4 shrink-0 text-(--color-content-muted)" aria-hidden="true" />
            <span className="min-w-0">
              {team.teamName}
              <span className="ml-1 text-xs font-normal text-(--color-content-muted)">
                · {countLabel(team.members.length, 'pessoa', 'pessoas')}
              </span>
            </span>
          </label>
        </td>
        <td colSpan={days.length} />
        <td className="tabular p-2 text-right font-medium">
          {Hours.ofQuarters(team.quarterHours).format()}
        </td>
      </tr>
      <ActivityRows activities={team.activities} days={days} dimmed={none} />
      <tr>
        <td colSpan={days.length + 2} className="pb-2 pl-8 pr-2">
          <ul
            className="flex flex-wrap gap-x-4 gap-y-1 text-xs"
            aria-label={`Quem recebe pela equipe ${team.teamName}`}
          >
            {team.members.map((member) => (
              <MemberToggle
                key={member.resourceId}
                member={member}
                included={!excluded.has(member.resourceId)}
                disabled={disabled}
                onToggle={() => onToggleMember(member.resourceId)}
              />
            ))}
          </ul>
        </td>
      </tr>
    </>
  );
}

function MemberToggle({
  member,
  included,
  disabled,
  onToggle,
}: {
  member: ProposalPerson;
  included: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <li className={cn(!included && 'opacity-50')}>
      <label className="flex min-h-11 items-center gap-1.5 md:min-h-0">
        <input
          type="checkbox"
          checked={included}
          disabled={disabled}
          onChange={onToggle}
          aria-label={`Incluir ${member.resourceName} na alocação automática`}
        />
        {member.resourceName}
        <span className="tabular text-(--color-content-muted)">
          {Hours.ofQuarters(member.quarterHours).format()}
        </span>
      </label>
    </li>
  );
}

function ResourceGroup({
  group,
  days,
  included,
  disabled,
  onToggle,
}: {
  group: ProposalPerson;
  days: readonly BoardDay[];
  included: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className={cn('bg-(--color-surface-muted)', !included && 'opacity-50')}>
        <td className="p-2">
          <label className="flex items-center gap-2 font-medium">
            <input
              type="checkbox"
              checked={included}
              disabled={disabled}
              onChange={onToggle}
              aria-label={`Incluir ${group.resourceName} na alocação automática`}
            />
            {group.resourceName}
          </label>
        </td>
        <td colSpan={days.length} />
        <td className="tabular p-2 text-right font-medium">
          {Hours.ofQuarters(group.quarterHours).format()}
        </td>
      </tr>
      <ActivityRows activities={group.activities} days={days} dimmed={!included} />
    </>
  );
}

/** Chamariz da proposta no topo do quadro — some quando não há o que propor. */
export function AutoAllocationBanner({
  proposal,
  byTeam = false,
  onOpen,
}: {
  proposal: AutoAllocationPlan | undefined;
  /** Na obra por equipe a faixa conta equipes e pessoas, e não funcionários (RN-432). */
  byTeam?: boolean;
  onOpen: () => void;
}) {
  if (proposal === undefined || proposal.lines.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-(--radius-card) border border-(--color-border-subtle) bg-(--color-surface-muted) p-3">
      <Badge tone="info" icon={<Wand2 aria-hidden="true" />}>
        Cronograma
      </Badge>
      <p className="text-sm">
        {countLabel(proposal.activityCount, 'atividade está', 'atividades estão')} em execução nesta
        semana. Há proposta pronta para{' '}
        {byTeam
          ? teamsAndPeopleLabel(proposal.teamCount, proposal.individualCount)
          : countLabel(proposal.resourceCount, 'funcionário', 'funcionários')}{' '}
        — {Hours.ofQuarters(proposal.quarterHours).format()}.
      </p>
      <Button variant="secondary" size="sm" className="ml-auto" onClick={onOpen}>
        Revisar proposta
      </Button>
    </div>
  );
}
